import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import BWModalAlert from './BWModalAlert';
import BWModalBody from './BWModalBody';
import BWModalFooter, { modalActionPropType } from './BWModalFooter';
import BWModalHeader from './BWModalHeader';
import { BW_MODAL_SIZE_NAMES, DEFAULT_CLOSE_CONFIRMATION, FOCUSABLE_SELECTOR } from './constants';
import './style.scss';

let openModalCount = 0;
let previousBodyOverflow = '';

const normalizeCloseConfirmation = (content) => ({
  ...DEFAULT_CLOSE_CONFIRMATION,
  ...(typeof content === 'string' ? { description: content } : content || {}),
});

const getFocusableElements = (container) =>
  Array.from(container?.querySelectorAll(FOCUSABLE_SELECTOR) || []).filter(
    (element) => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true',
  );

/**
 * Modal foundation for Portal screens. Business components should prefer one of
 * BWConfirmModal, BWFormModal, BWWorkflowModal or BWDetailModal.
 *
 * Legacy `header`, string `footer` and `onConfirm` props remain supported.
 */
const BWModal = ({
  open,
  visible,
  title,
  header,
  description,
  subtitle,
  status,
  headerExtra,
  children,
  context,
  footer,
  onClose,
  onConfirm,
  size = 'md',
  width,
  loading = false,
  loadingLabel = 'Đang xử lý',
  error,
  closable = true,
  closeDisabled = false,
  maskClosable = true,
  keyboard = true,
  destroyOnClose = true,
  stickyHeader = true,
  stickyFooter = true,
  bodyClassName,
  modalClassName,
  className,
  bodyStyle,
  modalStyle,
  headerStyle,
  titleStyle,
  closeStyle,
  footerStyle,
  wrapperId = 'bw_modal_root',
  confirmBeforeClose = false,
  dirty = false,
  closeConfirmationContent,
  primaryAction,
  secondaryAction,
  cancelAction,
  dangerAction,
  initialFocusRef,
  initialFocusSelector,
  testId,
  zIndex,
}) => {
  const generatedId = useId().replace(/:/g, '');
  const titleId = `bw-modal-title-${generatedId}`;
  const descriptionId = description || subtitle ? `bw-modal-description-${generatedId}` : undefined;
  const isOpen = open ?? visible ?? false;
  const resolvedTitle = title ?? header ?? 'Thông báo';
  const resolvedDescription = description ?? subtitle;
  const isLegacy = title === undefined && header !== undefined;
  const legacyPrimaryAction =
    typeof footer === 'string'
      ? {
          label: footer,
          type: 'danger',
          onClick: onConfirm,
        }
      : null;
  const resolvedPrimaryAction = primaryAction || legacyPrimaryAction;
  const resolvedCancelAction = cancelAction || (isLegacy ? { label: 'Quay về' } : null);
  const hasActions = Boolean(resolvedCancelAction || secondaryAction || resolvedPrimaryAction || dangerAction);
  const isBusy = Boolean(
    loading ||
      resolvedPrimaryAction?.loading ||
      secondaryAction?.loading ||
      resolvedCancelAction?.loading ||
      dangerAction?.loading,
  );
  const customFooter = React.isValidElement(footer) ? footer : null;
  const showFooter = footer !== null && (Boolean(customFooter) || hasActions);
  const resolvedZIndex = zIndex ?? 'var(--portal-z-modal, 7000)';
  const nestedZIndex = typeof zIndex === 'number' ? zIndex + 10 : 'var(--portal-z-modal-nested, 7040)';
  const overlayRef = useRef(null);
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const pendingCloseRef = useRef(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const closeConfirmation = useMemo(
    () => normalizeCloseConfirmation(closeConfirmationContent),
    [closeConfirmationContent],
  );

  const performClose = useCallback(
    (reason, callback) => {
      if (typeof callback === 'function') callback(reason);
      else onClose?.(reason);
    },
    [onClose],
  );

  const requestClose = useCallback(
    (reason = 'closeButton', callback) => {
      if (isBusy || closeDisabled) return;
      if (confirmBeforeClose && dirty) {
        pendingCloseRef.current = { reason, callback };
        setConfirmCloseOpen(true);
        return;
      }
      performClose(reason, callback);
    },
    [closeDisabled, confirmBeforeClose, dirty, isBusy, performClose],
  );

  const discardAndClose = useCallback(() => {
    const pendingClose = pendingCloseRef.current || { reason: 'discard' };
    pendingCloseRef.current = null;
    setConfirmCloseOpen(false);
    performClose(pendingClose.reason, pendingClose.callback);
  }, [performClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    restoreFocusRef.current = document.activeElement;
    if (openModalCount === 0) {
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModalCount += 1;

    const focusTimer = window.requestAnimationFrame(() => {
      const preferredFocus =
        initialFocusRef?.current ||
        (initialFocusSelector ? containerRef.current?.querySelector(initialFocusSelector) : null) ||
        titleRef.current;
      preferredFocus?.focus?.();
    });

    return () => {
      window.cancelAnimationFrame(focusTimer);
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) document.body.style.overflow = previousBodyOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [initialFocusRef, initialFocusSelector, isOpen]);

  useEffect(() => {
    if (!isOpen) setConfirmCloseOpen(false);
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.target.closest('[data-bw-modal-overlay]') !== overlayRef.current) return;

    if (event.key === 'Escape' && keyboard) {
      event.preventDefault();
      requestClose('escape');
      return;
    }

    if (event.key !== 'Tab') return;
    const focusableElements = getFocusableElements(containerRef.current);
    if (!focusableElements.length) {
      event.preventDefault();
      titleRef.current?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && (document.activeElement === firstElement || document.activeElement === titleRef.current)) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const handleMaskClick = (event) => {
    if (maskClosable && event.target === event.currentTarget) requestClose('mask');
  };

  if ((!isOpen && destroyOnClose) || typeof document === 'undefined') return null;

  const portalTarget = document.getElementById(wrapperId) || document.body;
  const containerStyle = {
    ...modalStyle,
    ...(width ? { '--bw-modal-custom-width': typeof width === 'number' ? `${width}px` : width } : {}),
  };

  return createPortal(
    <>
      <div
        ref={overlayRef}
        className={classNames('bw_modal bw_modal_ds_overlay', {
          bw_modal_open: isOpen,
          bw_modal_ds_legacy: isLegacy,
        })}
        data-bw-modal-overlay
        data-testid={testId}
        aria-hidden={!isOpen}
        style={{ '--bw-modal-z-index': resolvedZIndex, zIndex: resolvedZIndex }}
        onMouseDown={handleMaskClick}
        onKeyDown={handleKeyDown}>
        <div
          ref={containerRef}
          className={classNames(
            'bw_modal_container',
            'bw_modal_ds_container',
            `bw_modal_ds_${size}`,
            {
              bw_modal_ds_custom_width: Boolean(width),
              bw_modal_ds_has_context: Boolean(context),
              bw_modal_ds_sticky_header: stickyHeader,
              bw_modal_ds_sticky_footer: stickyFooter,
            },
            modalClassName,
            className,
          )}
          style={containerStyle}
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          aria-busy={isBusy}>
          <BWModalHeader
            ref={titleRef}
            title={resolvedTitle}
            description={resolvedDescription}
            status={status}
            headerExtra={headerExtra}
            closable={closable}
            closeDisabled={isBusy || closeDisabled}
            onClose={() => requestClose('closeButton')}
            titleId={titleId}
            descriptionId={descriptionId}
            headerStyle={headerStyle}
            titleStyle={titleStyle}
            closeStyle={closeStyle}
          />
          {context ? <div className='bw_modal_ds_context'>{context}</div> : null}
          <BWModalBody className={bodyClassName} loading={loading} loadingLabel={loadingLabel} style={bodyStyle}>
            {error ? (
              <BWModalAlert
                tone='danger'
                title={typeof error === 'object' ? error.title : 'Không thể hoàn tất thao tác'}
                description={typeof error === 'object' ? error.description : error}
              />
            ) : null}
            {children}
          </BWModalBody>
          {showFooter ? (
            <BWModalFooter
              cancelAction={resolvedCancelAction}
              secondaryAction={secondaryAction}
              primaryAction={resolvedPrimaryAction}
              dangerAction={dangerAction}
              style={footerStyle}
              onCancel={() => requestClose('cancelAction', resolvedCancelAction?.onClick)}>
              {customFooter}
            </BWModalFooter>
          ) : null}
        </div>
      </div>

      {confirmCloseOpen ? (
        <BWModal
          open
          title={closeConfirmation.title}
          description={closeConfirmation.description}
          size='sm'
          zIndex={nestedZIndex}
          maskClosable={false}
          confirmBeforeClose={false}
          onClose={() => setConfirmCloseOpen(false)}
          cancelAction={{
            label: closeConfirmation.continueLabel,
          }}
          dangerAction={{
            label: closeConfirmation.discardLabel,
            onClick: discardAndClose,
          }}>
          <BWModalAlert
            tone='warning'
            title='Thông tin chưa được lưu'
            description='Hãy kiểm tra trước khi bỏ các thay đổi trong cửa sổ hiện tại.'
          />
        </BWModal>
      ) : null}
    </>,
    portalTarget,
  );
};

BWModal.propTypes = {
  open: PropTypes.bool,
  visible: PropTypes.bool,
  title: PropTypes.node,
  header: PropTypes.node,
  description: PropTypes.node,
  subtitle: PropTypes.node,
  status: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
  headerExtra: PropTypes.node,
  children: PropTypes.node,
  context: PropTypes.node,
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  size: PropTypes.oneOf(BW_MODAL_SIZE_NAMES),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  loading: PropTypes.bool,
  loadingLabel: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  closable: PropTypes.bool,
  closeDisabled: PropTypes.bool,
  maskClosable: PropTypes.bool,
  keyboard: PropTypes.bool,
  destroyOnClose: PropTypes.bool,
  stickyHeader: PropTypes.bool,
  stickyFooter: PropTypes.bool,
  bodyClassName: PropTypes.string,
  modalClassName: PropTypes.string,
  className: PropTypes.string,
  bodyStyle: PropTypes.object,
  modalStyle: PropTypes.object,
  headerStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  closeStyle: PropTypes.object,
  footerStyle: PropTypes.object,
  wrapperId: PropTypes.string,
  confirmBeforeClose: PropTypes.bool,
  dirty: PropTypes.bool,
  closeConfirmationContent: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  primaryAction: modalActionPropType,
  secondaryAction: modalActionPropType,
  cancelAction: modalActionPropType,
  dangerAction: modalActionPropType,
  initialFocusRef: PropTypes.shape({ current: PropTypes.any }),
  initialFocusSelector: PropTypes.string,
  testId: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default BWModal;
